import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import api from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Stamp, Undo2, Eye } from "lucide-react";
import { useAuth } from "../lib/auth";
import { DISPLAY_MODEL } from "../lib/brand";

export default function Approvals() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [target, setTarget] = useState(null);
  const [action, setAction] = useState(""); // Approve | Return
  const [remarks, setRemarks] = useState("");
  const [finalText, setFinalText] = useState("");
  const navigate = useNavigate();

  const reload = () => api.get("/approvals").then((r) => setQueue(r.data));
  useEffect(() => { reload(); }, []);

  const submit = async () => {
    try {
      await api.post(`/drafts/${target.draft_version_id}/approval`, {
        action, officer_remarks: remarks, final_decision_text: finalText,
      });
      toast.success(`Draft ${action}d`);
      setTarget(null); setAction(""); setRemarks(""); setFinalText("");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <Layout breadcrumbs={["SSRD", "Approvals"]}>
      <div className="mb-6">
        <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Officer</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="approvals-title">Approvals Queue</h1>
        <p className="text-sm text-muted-foreground">
          Drafts pending officer approval. Approving finalizes the case; returning sends it back for correction.
        </p>
      </div>

      {!["Officer", "Admin"].includes(user?.role) && (
        <div className="mb-4 text-xs rounded-md border border-amber-200 bg-amber-50 text-amber-900 p-3" data-testid="role-warning">
          You are logged in as <strong>{user?.role}</strong>. Approval actions require <strong>Officer</strong> or <strong>Admin</strong> role.
        </div>
      )}

      <Card><CardContent className="p-0">
        <Table data-testid="approvals-table">
          <TableHeader><TableRow>
            <TableHead>Case</TableHead><TableHead>Subject</TableHead>
            <TableHead>Version</TableHead><TableHead>Engine</TableHead>
            <TableHead>Submitted</TableHead><TableHead className="text-right">Action</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {queue.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">No drafts pending approval.</TableCell></TableRow>}
            {queue.map((q) => (
              <TableRow key={q.draft_version_id}>
                <TableCell className="font-mono text-xs">{q.case_number}</TableCell>
                <TableCell className="text-xs">{q.subject_title}</TableCell>
                <TableCell>v{q.version_no}</TableCell>
                <TableCell className="text-xs font-mono">{DISPLAY_MODEL}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{(q.generated_at || "").replace("T", " ").slice(0, 16)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => navigate(`/cases/${q.case_id}/review/${q.draft_version_id}`)} className="gap-1" data-testid={`view-${q.draft_version_id}`}><Eye className="size-3.5" /> Open</Button>
                    <Button size="sm" onClick={() => { setTarget(q); setAction("Approve"); }} className="gap-1" data-testid={`approve-${q.draft_version_id}`}><Stamp className="size-3.5" /> Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => { setTarget(q); setAction("Return"); }} className="gap-1" data-testid={`return-${q.draft_version_id}`}><Undo2 className="size-3.5" /> Return</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={!!target} onOpenChange={(o) => { if (!o) setTarget(null); }}>
        <DialogContent data-testid="approval-dialog">
          <DialogHeader>
            <DialogTitle>{action === "Approve" ? "Approve Draft" : "Return for Correction"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-border bg-secondary/30 p-3 text-xs">
              <div><strong>Case:</strong> {target?.case_number}</div>
              <div><strong>Subject:</strong> {target?.subject_title}</div>
              <div><strong>Draft v{target?.version_no}</strong> · {DISPLAY_MODEL}</div>
            </div>
            <div>
              <Label className="text-xs">Officer Remarks</Label>
              <Textarea rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="approval-remarks" />
            </div>
            {action === "Approve" && (
              <div>
                <Label className="text-xs">Final Decision Text (optional override)</Label>
                <Textarea rows={3} value={finalText} onChange={(e) => setFinalText(e.target.value)} placeholder="Officer's final operative paragraph…" data-testid="approval-final-decision" />
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">
              Remi8-generated draft is not final until reviewed and approved by the authorized officer.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>Cancel</Button>
            <Button onClick={submit} data-testid="approval-confirm-btn">{action}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
